import { BalanceData, CommandResult, Fix64, GearBonus, LevelPhase, ReplayError, TimedCommand, commandToString, isModifiersJson } from "@highmarch/domain";
import { LevelSession, ReplayRecord, StageMark, recordCommands, recordModifiers, recordOwnedTowers } from "./levelSession";

/** Bumped when the suspended-battle shape changes; an older or newer one is dropped on load (never half-read). */
export const SUSPEND_FORMAT = 1;

/**
 * One tower's gear bonus with each Fix64 as its raw value in decimal (JSON has no bigint). `troops` (VIP troop strength
 * on the base, docs/SHOP.md §8.5) only when present, so a VIP-0 snapshot is written exactly as before.
 */
export interface GearBonusJson { damage: string; range: string; fireRate: string; hp: string; points: string; mythic: number[]; setBonus: boolean; troops?: string }

/**
 * A battle put aside (a wave break, the page hidden): the run's inputs and command log up to `record.ticks`, enough to
 * rebuild it by deterministic replay. Everything the sim was built from is kept — tower levels and gear may change in
 * the hub before the player comes back, and the resumed run must be the same run.
 */
export interface SuspendedLevel {
  formatVersion: number;
  record: ReplayRecord;
  gear: GearBonusJson[];
  /** The wave the run was on (0 before START WAVES) and the level's wave count: the battle card shows them without a replay. */
  stage: number;
  stages: number;
  savedAt: number;
  /** The attempt has spent its checkpoint retry (DESIGN §4.8.8); absent = not yet. */
  checkpointUsed?: boolean;
  /** MP-06: retries spent when more than one (a War Banner's second); absent = 1 if `checkpointUsed`, else 0. */
  checkpointsUsed?: number;
}

/** The checkpoint fields of a snapshot: written exactly as before for 0 or 1 retries spent. */
function checkpointFields(used: number): Pick<SuspendedLevel, "checkpointUsed" | "checkpointsUsed"> {
  return used <= 0 ? {} : used === 1 ? { checkpointUsed: true } : { checkpointUsed: true, checkpointsUsed: used };
}

const FIX_KEYS = ["damage", "range", "fireRate", "hp", "points"] as const;

export function gearBonusToJson(g: GearBonus): GearBonusJson {
  return {
    damage: g.damage.raw.toString(), range: g.range.raw.toString(), fireRate: g.fireRate.raw.toString(), hp: g.hp.raw.toString(), points: g.points.raw.toString(), mythic: [...g.mythic], setBonus: g.setBonus,
    ...(g.troops === undefined ? {} : { troops: g.troops.raw.toString() }),
  };
}

export function gearBonusFromJson(j: GearBonusJson): GearBonus {
  const f = (k: (typeof FIX_KEYS)[number]) => Fix64.fromRaw(BigInt(j[k]));
  return {
    damage: f("damage"), range: f("range"), fireRate: f("fireRate"), hp: f("hp"), points: f("points"), mythic: [...j.mythic], setBonus: j.setBonus,
    ...(j.troops === undefined ? {} : { troops: Fix64.fromRaw(BigInt(j.troops)) }),
  };
}

/** Snapshot of a live session as a suspended battle. */
export function suspendSession(session: LevelSession, savedAt: number): SuspendedLevel {
  return { formatVersion: SUSPEND_FORMAT, record: session.toRecord(), gear: session.gear.map(gearBonusToJson), stage: session.sim.stage, stages: session.sim.stages, savedAt, ...checkpointFields(session.checkpointsUsed) };
}

/**
 * Anti-save-scum: a battle's log only grows. A snapshot of the same run (level and seed) that is behind the stored
 * one never replaces it, so no path — a stale tab, a late pagehide — winds a run back to before a mistake.
 */
export function mayReplace(stored: SuspendedLevel | undefined, next: SuspendedLevel): boolean {
  if (!stored || stored.record.level !== next.record.level || stored.record.seed !== next.record.seed) return true;
  return next.record.ticks >= stored.record.ticks;
}

/**
 * Rebuilds a suspended battle: a fresh session with the stored inputs, and the replay that brings it back to the
 * stored tick. The replay goes through the session itself, so its log (and leak tally) is the original run's.
 */
export function resumeSession(balance: BalanceData, s: SuspendedLevel): { session: LevelSession; replay: ResumeReplay } {
  const r = s.record;
  const towerLevel = typeof r.towerLevel === "number" ? r.towerLevel : [...r.towerLevel];
  const session = new LevelSession(balance, r.level, BigInt(r.seed), recordOwnedTowers(balance, r), towerLevel, r.firstClear, s.gear.map(gearBonusFromJson), recordModifiers(balance, r));
  session.checkpointsUsed = s.checkpointsUsed ?? (s.checkpointUsed === true ? 1 : 0);
  return { session, replay: new ResumeReplay(session, recordCommands(r), r.ticks, r.hash) };
}

/**
 * Replays a command log onto a fresh session up to `toTick`, in chunks the caller sizes (the client yields a frame
 * between chunks and draws a progress bar). Same rule as domain `runReplay`: step to each command's tick, execute it,
 * refuse anything out of order or rejected.
 */
export class ResumeReplay {
  private next = 0;
  private finished = false;

  /** `expectHash`: the stored state hash (hex) the rebuilt run must reach, or it is refused like a log that does not fit. */
  constructor(private readonly session: LevelSession, private readonly commands: readonly TimedCommand[], readonly toTick: number, private readonly expectHash?: string) {}

  get done(): boolean { return this.finished; }
  /** 0..1 of the ticks replayed so far. */
  get progress(): number { return this.toTick <= 0 ? 1 : Math.min(1, this.session.sim.tick / this.toTick); }

  /** Replays up to `maxTicks` more ticks; returns whether the session has reached `toTick`. Throws ReplayError on a log that does not fit. */
  advance(maxTicks: number): boolean {
    const sim = this.session.sim;
    let budget = maxTicks;
    while (!this.finished) {
      while (this.next < this.commands.length && this.commands[this.next].tick === sim.tick) {
        const c = this.commands[this.next++].command;
        const r = this.session.execute(c);
        if (r !== CommandResult.Ok) throw new ReplayError(`Resume: ${commandToString(c)} at tick ${sim.tick} failed: ${CommandResult[r]}`);
      }
      if (this.next < this.commands.length && this.commands[this.next].tick < sim.tick) throw new ReplayError(`Resume: command at tick ${this.commands[this.next].tick} is out of order (sim at ${sim.tick})`);
      if (sim.tick >= this.toTick) {
        if (this.next < this.commands.length) throw new ReplayError(`Resume: ${this.commands.length - this.next} commands after the saved tick ${this.toTick}`);
        const hash = sim.computeHash().toString(16).padStart(16, "0");
        if (this.expectHash !== undefined && hash !== this.expectHash) throw new ReplayError(`Resume: rebuilt state ${hash} is not the saved ${this.expectHash}`);
        this.finished = true;
        break;
      }
      if (sim.isOver) throw new ReplayError(`Resume: the level ended at tick ${sim.tick}, before the saved tick ${this.toTick}`);
      if (budget-- <= 0) break;
      this.session.tick();
    }
    return this.finished;
  }

  /** Replays the whole remainder at once (tests, and short logs). */
  runToEnd(): void { while (!this.advance(Number.MAX_SAFE_INTEGER)) { /* advance throws rather than stall */ } }
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isRawFix = (v: unknown): boolean => typeof v === "string" && /^-?\d+$/.test(v);

/** A stored suspended battle, or undefined when it is missing, from another format or malformed (it is then dropped). */
export function parseSuspended(v: unknown): SuspendedLevel | undefined {
  if (typeof v !== "object" || v === null) return undefined;
  const s = v as Partial<SuspendedLevel>;
  const r = s.record as Partial<ReplayRecord> | undefined;
  if (s.formatVersion !== SUSPEND_FORMAT || !isNum(s.stage) || !isNum(s.stages) || !isNum(s.savedAt) || typeof r !== "object" || r === null) return undefined;
  if (!isNum(r.level) || !isNum(r.ticks) || typeof r.seed !== "string" || !/^\d+$/.test(r.seed) || typeof r.firstClear !== "boolean") return undefined;
  if (!(isNum(r.towerLevel) || (Array.isArray(r.towerLevel) && r.towerLevel.every(isNum)))) return undefined;
  if (!Array.isArray(r.ownedTowers) || !r.ownedTowers.every((x) => typeof x === "string")) return undefined;
  const cmdOk = (c: unknown) => Array.isArray(c) && c.length === 5 && isNum(c[0]) && typeof c[1] === "string" && isNum(c[2]) && isNum(c[3]) && isNum(c[4]);
  if (!Array.isArray(r.commands) || !r.commands.every(cmdOk)) return undefined;
  const gearOk = (g: unknown) => typeof g === "object" && g !== null && FIX_KEYS.every((k) => isRawFix((g as Record<string, unknown>)[k]))
    && Array.isArray((g as GearBonusJson).mythic) && (g as GearBonusJson).mythic.every(isNum) && typeof (g as GearBonusJson).setBonus === "boolean"
    && ((g as GearBonusJson).troops === undefined || isRawFix((g as GearBonusJson).troops));
  if (!Array.isArray(s.gear) || !s.gear.every(gearOk)) return undefined;
  if (r.modifiers !== undefined && !isModifiersJson(r.modifiers)) return undefined;
  if (s.checkpointUsed !== undefined && typeof s.checkpointUsed !== "boolean") return undefined;
  if (s.checkpointsUsed !== undefined && (!Number.isInteger(s.checkpointsUsed) || s.checkpointsUsed < 0)) return undefined;
  return s as SuspendedLevel;
}

// ------------------------------------------------------------------ checkpoint retry (DESIGN §4.8.8)

/**
 * Where a lost run may restart: the latest mark it passed among "every `checkpointEveryStages` stages cleared" and "all
 * but the boss stage cleared". Returns the stages cleared at that mark (the retry plays stage mark + 1), 0 for none.
 */
export function checkpointMark(balance: BalanceData, stagesCleared: number, stages: number): number {
  const every = balance.economy.checkpointEveryStages;
  if (every <= 0) return 0;
  const bossMark = stages - 1;
  if (stagesCleared >= bossMark && bossMark > 0) return bossMark;
  return Math.floor(Math.min(stagesCleared, stages - 1) / every) * every;
}

/** A lost run's retry point: the wave the retry starts on and the mark behind it. */
export interface Checkpoint { readonly wave: number; readonly mark: StageMark }

/**
 * The checkpoint a finished run offers, or null: only a loss of a level (never Iron or Endless), once per attempt (a War
 * Banner's run twice, MP-06; the last retry itself offers none),
 * only from a mark the run passed (the run's own StageMark, so no search replay is needed).
 */
export function retryCheckpoint(session: LevelSession): Checkpoint | null {
  const sim = session.sim;
  if (sim.phase !== LevelPhase.Lost || session.checkpointsUsed >= session.checkpointsAllowed) return null;
  // Iron and Endless have no checkpoint retry (docs/MODES.md): Iron is one try a day, an Endless score is one unbroken run.
  if (sim.isIron || sim.isEndless) return null;
  // Overlapping stages (DESIGN §3.2, M30 #19) clear together, so the run may have passed the mark without stopping on
  // it: the retry then starts at the latest clear before it.
  let mark = checkpointMark(session.balance, sim.stagesCleared, sim.stages);
  let m: StageMark | undefined;
  for (; mark > 0 && (m = session.stageMark(mark)) === undefined; mark--);
  return m ? { wave: mark + 1, mark: m } : null;
}

/**
 * The lost run cut back to its checkpoint, as a suspended battle the resume path rebuilds by replay: the same inputs
 * and seed, the commands before the mark's tick, the mark's state hash to prove it (ResumeReplay), the retry spent.
 */
export function checkpointFrom(session: LevelSession, cp: Checkpoint, savedAt: number): SuspendedLevel {
  const full = session.toRecord();
  const record: ReplayRecord = {
    ...full, hash: cp.mark.hash, phase: LevelPhase[LevelPhase.BetweenWaves], ticks: cp.mark.tick, stagesCleared: cp.mark.stagesCleared,
    kills: cp.mark.kills, leaks: cp.mark.leaks, commands: full.commands.filter(([tick]) => tick < cp.mark.tick),
  };
  return { formatVersion: SUSPEND_FORMAT, record, gear: session.gear.map(gearBonusToJson), stage: cp.mark.stagesCleared, stages: session.sim.stages, savedAt, ...checkpointFields(session.checkpointsUsed + 1) };
}
